import JSZip from 'jszip';
import { PPTX_REGEX } from '@src/constants/presentation';
import { NOTE_MAX_LENGTH } from '@src/constants/project';

export const getSlideCountFromPptx = async (file) => {
  try {
    const zip = await JSZip.loadAsync(file);
    const slideFiles = Object.keys(zip.files).filter((name) =>
      PPTX_REGEX.SLIDE_FILE.test(name),
    );
    return slideFiles.length;
  } catch {
    return 0;
  }
};

// Drop auto fields (slide number, date, footer) so they are not mistaken for
// real speaker-note content.
const stripNoteFields = (xml) => xml.replace(PPTX_REGEX.NOTE_FIELD, '');

const extractNoteText = (xml) => {
  const matches = stripNoteFields(xml).match(PPTX_REGEX.NOTE_TEXT_TAG) || [];
  return matches.map((m) => m.replace(PPTX_REGEX.NOTE_TEXT_STRIP, '')).join('');
};

const hasNoteContent = (xml) => extractNoteText(xml).trim().length > 0;

export const getOverlimitNotesSlides = async (
  file,
  maxLength = NOTE_MAX_LENGTH,
) => {
  const zip = await JSZip.loadAsync(file);
  const slideFiles = Object.keys(zip.files)
    .filter((name) => PPTX_REGEX.SLIDE_FILE.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.match(PPTX_REGEX.SLIDE_NUMBER)[0], 10);
      const numB = parseInt(b.match(PPTX_REGEX.SLIDE_NUMBER)[0], 10);
      return numA - numB;
    });

  const results = await Promise.all(
    slideFiles.map(async (slidePath, index) => {
      const slideName = slidePath.replace(PPTX_REGEX.SLIDE_PREFIX, '');
      const relsFile = zip.files[`ppt/slides/_rels/${slideName}.rels`];
      if (!relsFile) return null;

      const relsXml = await relsFile.async('string');
      const notesMatch = relsXml.match(PPTX_REGEX.NOTE_REL_TARGET);
      if (!notesMatch) return null;

      const notesFile = zip.files[`ppt/notesSlides/${notesMatch[1]}`];
      if (!notesFile) return null;

      const notesXml = await notesFile.async('string');
      return extractNoteText(notesXml).length > maxLength ? index + 1 : null;
    }),
  );

  return results.filter((pos) => pos !== null);
};

export const getNoteCountFromPptx = async (file) => {
  try {
    const zip = await JSZip.loadAsync(file);
    const noteFiles = Object.keys(zip.files).filter((name) =>
      PPTX_REGEX.NOTE_FILE.test(name),
    );
    const noteContents = await Promise.all(
      noteFiles.map((name) => zip.files[name].async('string')),
    );
    return noteContents.filter(hasNoteContent).length;
  } catch {
    return 0;
  }
};

export const calculatePresentationCredit = ({
  enableAI,
  slideCount,
  model,
}) => {
  if (!enableAI || !slideCount) return 0;
  const creditPerSlide = model?.creditPerSlide;
  if (typeof creditPerSlide !== 'number' || creditPerSlide <= 0) return 0;
  return creditPerSlide * slideCount;
};
